import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Loading, ScoreRing } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ChevronRight, CheckCircle2, XCircle, RotateCcw, Target } from "lucide-react";

export default function MistakePractice() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sn = params.get("sn") || "all";
  const topic = params.get("topic") || "all";

  const [all, setAll] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);

  useEffect(() => { api.get("/student/mistakes").then((r) => setAll(r.data)); }, []);

  const questions = useMemo(() => {
    if (!all) return [];
    const seen = new Set();
    return all.filter((m) => {
      if (sn !== "all" && m.section_name !== sn) return false;
      if (topic !== "all" && m.topic !== topic) return false;
      if (seen.has(m.qid)) return false;
      seen.add(m.qid);
      return true;
    });
  }, [all, sn, topic]);

  if (!all) return <Loading />;
  if (questions.length === 0)
    return (
      <div className="animate-fade-up">
        <button onClick={() => navigate("/student/mistakes")} className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#7C3AED] hover:underline"><ArrowLeft className="h-4 w-4" /> Back</button>
        <div className="ember-card grid place-items-center py-16 text-center"><CheckCircle2 className="h-10 w-10 text-[#059669]" /><p className="mt-3 font-semibold text-[#524B6B]">Nothing to practise here — you're on top of it!</p></div>
      </div>
    );

  const q = questions[idx];
  const chosen = answers[q?.qid];

  const score = questions.filter((x) => (answers[x.qid] || "").toLowerCase() === (x.correct_answer || "").toLowerCase()).length;
  const pct = Math.round((100 * score) / questions.length);

  if (done) {
    return (
      <div className="animate-fade-up max-w-3xl">
        <button onClick={() => navigate("/student/mistakes")} className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#7C3AED] hover:underline"><ArrowLeft className="h-4 w-4" /> Back to Mistake Bank</button>
        <div className="ember-card overflow-hidden">
          <div className="ember-gradient-soft flex flex-wrap items-center gap-8 p-8">
            <ScoreRing pct={pct} />
            <div>
              <h1 className="font-heading text-3xl font-extrabold text-[#1E1B2E]">Practice complete</h1>
              <p className="mt-1 text-[#524B6B]">You got <b className="text-[#1E1B2E]">{score}/{questions.length}</b> of your past mistakes right this time.</p>
              <Button onClick={() => { setAnswers({}); setIdx(0); setDone(false); }} data-testid="retry-again-btn" className="mt-4 h-10 rounded-xl ember-gradient font-bold"><RotateCcw className="mr-1.5 h-4 w-4" /> Practise again</Button>
            </div>
          </div>
        </div>
        <div className="mt-6 space-y-4">
          {questions.map((m, i) => {
            const your = answers[m.qid];
            const correct = (your || "").toLowerCase() === (m.correct_answer || "").toLowerCase();
            return (
              <div key={m.qid} className="ember-card p-6" data-testid={`practice-review-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="rounded-full bg-[#FFFBEB] px-2.5 py-1 text-xs font-semibold text-[#B45309]">{m.topic}</span>
                  {correct ? <span className="inline-flex items-center gap-1 text-xs font-bold text-[#059669]"><CheckCircle2 className="h-4 w-4" /> Correct now</span> : <span className="inline-flex items-center gap-1 text-xs font-bold text-[#E11D48]"><XCircle className="h-4 w-4" /> Still tricky</span>}
                </div>
                <p className="mt-2 font-heading font-bold text-[#1E1B2E]">{m.text}</p>
                <div className="mt-3 space-y-2">
                  {m.options.map((o) => {
                    const isC = o.key.toLowerCase() === (m.correct_answer || "").toLowerCase();
                    const isY = o.key.toLowerCase() === (your || "").toLowerCase();
                    let cls = "border-[#E9E5F5]";
                    if (isC) cls = "border-[#059669] bg-[#ECFDF5]";
                    else if (isY) cls = "border-[#E11D48] bg-[#FFF1F2]";
                    return <div key={o.key} className={`flex items-center gap-3 rounded-xl border-2 p-3 ${cls}`}><span className="grid h-7 w-7 place-items-center rounded-lg border text-xs font-bold text-[#524B6B]">{o.key}</span><span className="flex-1 text-sm text-[#1E1B2E]">{o.text}</span></div>;
                  })}
                </div>
                {m.explanation && <div className="mt-3 rounded-xl bg-[#F5F3FF] p-4 text-sm text-[#1E1B2E]"><b className="text-[#5B21B6]">Explanation:</b> {m.explanation}</div>}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-up max-w-3xl">
      <button onClick={() => navigate("/student/mistakes")} className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#7C3AED] hover:underline"><ArrowLeft className="h-4 w-4" /> Exit practice</button>
      <div className="ember-card p-6 sm:p-8">
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 text-sm font-bold text-[#7C3AED]"><Target className="h-4 w-4" /> Retrying mistake {idx + 1} of {questions.length}</span>
          <span className="rounded-full bg-[#FFFBEB] px-2.5 py-1 text-xs font-semibold text-[#B45309]">{q.topic}</span>
        </div>
        <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-[#EDE9FE]"><div className="h-full ember-gradient" style={{ width: `${(idx / questions.length) * 100}%`, transition: "width .3s" }} /></div>
        <p className="mt-5 font-heading text-xl font-bold leading-relaxed text-[#1E1B2E]" data-testid="practice-question">{q.text}</p>
        <div className="mt-6 space-y-3">
          {q.options.map((o) => {
            const selected = chosen === o.key;
            return (
              <button key={o.key} onClick={() => setAnswers({ ...answers, [q.qid]: o.key })} data-testid={`practice-option-${o.key}`}
                className={`flex w-full items-center gap-4 rounded-2xl border-2 p-4 text-left transition-all ${selected ? "border-[#7C3AED] bg-[#F5F3FF]" : "border-[#E9E5F5] hover:border-[#C4B5FD] hover:bg-[#FAF9FE]"}`}>
                <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl border-2 text-sm font-bold ${selected ? "border-[#7C3AED] bg-[#7C3AED] text-white" : "border-[#DDD6FE] text-[#7C3AED]"}`}>{o.key}</span>
                <span className="font-medium text-[#1E1B2E]">{o.text}</span>
              </button>
            );
          })}
        </div>
        <div className="mt-6 flex justify-end">
          {idx < questions.length - 1
            ? <Button disabled={!chosen} onClick={() => setIdx(idx + 1)} data-testid="practice-next" className="h-11 rounded-xl ember-gradient font-bold">Next <ChevronRight className="ml-1 h-4 w-4" /></Button>
            : <Button disabled={!chosen} onClick={() => setDone(true)} data-testid="practice-finish" className="h-11 rounded-xl ember-gradient font-bold">Finish practice</Button>}
        </div>
      </div>
    </div>
  );
}
